import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createLink } from '@meshconnect/web-link-sdk'
import {
  Copy, ChevronDown, CheckCircle2, XCircle, RefreshCw,
  Zap, Building2, Clock, ArrowLeft, CreditCard,
  ShieldCheck, AlertTriangle, Wifi, ExternalLink
} from 'lucide-react'
import {
  getMeshPayLinkTokenService,
  notifyMeshTransferService,
  generateCryptoPaymentTokenService,
  checkForumPayStatusService
} from 'network/services/payment.service'
import { generatePaymentToken } from 'redux-thunk/thunk/payment.thunk'
import { fetchUserInformation } from 'redux-thunk/thunk/user.thunk'
import { useBalanceSync } from 'hooks/useBalanceSync'
import useWalletSocketNameSpace from 'socket-resources/hooks/useWalletSocketNameSpace'
import ethImg  from 'assets/images/crypto-icons/eth.png'
import btcImg  from 'assets/images/crypto-icons/btc.png'
import usdcImg from 'assets/images/crypto-icons/usdc.png'
import solImg  from 'assets/images/crypto-icons/sol.png'
import ltcImg  from 'assets/images/crypto-icons/ltc.png'
import './CryptoDeposit.scss'

const CURRENCIES = [
  { symbol: 'ETH',  name: 'Ethereum', img: ethImg,  color: '#627EEA' },
  { symbol: 'BTC',  name: 'Bitcoin',  img: btcImg,  color: '#F7931A' },
  { symbol: 'USDC', name: 'USD Coin', img: usdcImg, color: '#2775CA' },
  { symbol: 'SOL',  name: 'Solana',   img: solImg,  color: '#9945FF' },
  { symbol: 'LTC',  name: 'Litecoin', img: ltcImg,  color: '#345D9D' },
]

const NETWORKS = {
  ETH:  [{ id: 'eth',  label: 'Ethereum', meshNetworkId: 'e3c7fdd8-b1fc-4e51-85ae-bb276e075611' }],
  BTC:  [{ id: 'btc',  label: 'Bitcoin',  meshNetworkId: null }],
  USDC: [
    { id: 'eth',  label: 'Ethereum', meshNetworkId: 'e3c7fdd8-b1fc-4e51-85ae-bb276e075611' },
    { id: 'base', label: 'Base',     meshNetworkId: 'aa883b03-120d-477c-a588-37c2afd3ca71' },
    { id: 'sol',  label: 'Solana',   meshNetworkId: '0291810a-5947-424d-9a59-e88bb33e999d' },
  ],
  SOL:  [{ id: 'sol',  label: 'Solana',   meshNetworkId: '0291810a-5947-424d-9a59-e88bb33e999d' }],
  LTC:  [{ id: 'ltc',  label: 'Litecoin', meshNetworkId: null }],
}

const ADDRESSES = {
  eth:  process.env.REACT_APP_MESH_ETH_ADDRESS  || '0x742d35Cc6634C0532925a3b844Bc454e4438f44e',
  base: process.env.REACT_APP_MESH_BASE_ADDRESS || '0x742d35Cc6634C0532925a3b844Bc454e4438f44e',
  sol:  process.env.REACT_APP_MESH_SOL_ADDRESS  || 'EV2dZFJG9oJGCFiNVBmFxHhRX3wKDuCzCnzaJQKaQVwR',
  btc:  process.env.REACT_APP_MESH_BTC_ADDRESS  || 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
  ltc:  process.env.REACT_APP_MESH_LTC_ADDRESS  || 'ltc1q8c6fshw2dlwun7ekn9qwf37cu2rn755upcp6el',
}

const shortId = (id) => {
  if (!id) return '—'
  const s = String(id)
  return s.length > 20 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s
}

const formatTime = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const CryptoDeposit = ({ isCAD = false }) => {
  const dispatch = useDispatch()
  const { user } = useSelector(state => state?.user)
  const { listenMeshTransferStatus } = useWalletSocketNameSpace()
  const { refreshBalance } = useBalanceSync()

  const [phase, setPhase]               = useState('select')
  const [errorMsg, setErrorMsg]         = useState('')
  const [currency, setCurrency]         = useState(CURRENCIES[0])
  const [networkIdx, setNetworkIdx]     = useState(0)
  const [showCurrDrop, setShowCurrDrop] = useState(false)
  const [showNetDrop,  setShowNetDrop]  = useState(false)
  const [copied, setCopied]             = useState(false)
  const [copiedField, setCopiedField]   = useState(null)
  const [showManual, setShowManual]     = useState(false)
  const [lastTransfer, setLastTransfer] = useState(null)
  const [transferStatus, setTransferStatus] = useState('pending')

  // Interac (CAD) specific state
  const [interacAmount, setInteracAmount] = useState('')
  const [interacLoading, setInteracLoading] = useState(false)
  const [interacError, setInteracError] = useState('')

  // ForumPay (Banking) specific state
  const [forumPayAmount, setForumPayAmount] = useState('')
  const [forumPayCrypto, setForumPayCrypto] = useState('BTC')
  const [forumPayResult, setForumPayResult] = useState(null)
  const [forumPayError, setForumPayError] = useState('')
  const [forumPayPolling, setForumPayPolling] = useState(false)
  const [pollState, setPollState] = useState(null)

  const transferDoneRef = useRef(false)

  // Always start clean — prevents HMR-preserved error state from persisting across popup opens
  useEffect(() => {
    setPhase('select')
    setErrorMsg('')
  }, [])

  // Poll ForumPay payment status every 5 s while address screen is open
  useEffect(() => {
    if (phase !== 'forumpay-address' || !forumPayResult?.paymentId) return
    setForumPayPolling(true)

    const intervalId = setInterval(async () => {
      try {
        const res = await checkForumPayStatusService(
          forumPayResult.paymentId,
          forumPayResult.address,
          forumPayResult.cryptoCurrency
        )
        const status = res?.status
        if (res?.state) setPollState(res.state)
        if (status === 'success') {
          clearInterval(intervalId)
          setForumPayPolling(false)
          setLastTransfer({
            method: 'forumpay',
            amount: res.credited ?? res.amount,
            symbol: res.currency || 'USD',
            transactionId: res.transactionId,
            timestamp: new Date()
          })
          setPhase('success')
          dispatch(fetchUserInformation())
          refreshBalance()
        } else if (status === 'failed' || status === 'cancelled') {
          clearInterval(intervalId)
          setForumPayPolling(false)
          setErrorMsg('Payment failed or was cancelled.')
          setPhase('error')
        } else if (status === 'error') {
          clearInterval(intervalId)
          setForumPayPolling(false)
          setErrorMsg(res?.message || 'Payment verification failed. Please start a new deposit.')
          setPhase('error')
        }
      } catch (_) {}
    }, 5000)

    return () => { clearInterval(intervalId); setForumPayPolling(false) }
  }, [phase, forumPayResult?.paymentId])

  // Subscribe to real-time webhook status once the Mesh deposit receipt is showing
  useEffect(() => {
    if (phase !== 'success' || lastTransfer?.method !== 'mesh' || !lastTransfer?.transactionId) return
    setTransferStatus('pending')

    const unsubscribe = listenMeshTransferStatus((event) => {
      if (event?.transactionId !== lastTransfer.transactionId) return
      const s = (event?.status || '').toLowerCase()

      // Update amount/symbol from the webhook (more accurate than widget callback value)
      if (event?.amount != null && Number(event.amount) > 0) {
        setLastTransfer(prev => ({
          ...prev,
          amount: Number(event.amount),
          symbol: event.symbol || prev.symbol
        }))
      }

      if (s === 'success' || s === 'succeeded' || s === 'completed') {
        setTransferStatus('confirmed')
        dispatch(fetchUserInformation())
      } else if (s === 'failed') {
        setTransferStatus('failed')
      }
    })

    return unsubscribe
  }, [phase, lastTransfer?.transactionId, lastTransfer?.method])

  const networks      = NETWORKS[currency.symbol] || []
  const activeNet     = networks[networkIdx] || networks[0]
  const depositAddr   = ADDRESSES[activeNet?.id] || ''
  const meshSupported = networks.some(n => n.meshNetworkId)

  const copyField = (value, key) => {
    if (!value) return
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(key)
      setTimeout(() => setCopiedField(null), 2000)
    })
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(depositAddr).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const selectCurrency = (c) => {
    setCurrency(c); setNetworkIdx(0); setShowCurrDrop(false); setShowManual(false)
  }

  const selectNetwork = (idx) => {
    setNetworkIdx(idx); setShowNetDrop(false)
  }

  const reset = () => {
    transferDoneRef.current = false
    setPhase('select'); setErrorMsg(''); setLastTransfer(null)
    setCopiedField(null); setInteracAmount(''); setInteracError(''); setTransferStatus('pending')
    setForumPayAmount(''); setForumPayCrypto('BTC'); setForumPayResult(null); setForumPayError(''); setForumPayPolling(false); setPollState(null)
  }

  // ── Mesh Pay ────────────────────────────────────────────────────────────────
  const handleMeshPay = useCallback(async () => {
    if (!user?.id) { setErrorMsg('Session not found. Please refresh.'); setPhase('error'); return }
    if (!meshSupported) {
      setErrorMsg(`Direct Crypto is not available for ${currency.symbol}. Use Banking or choose ETH, USDC, or SOL.`)
      setPhase('error'); return
    }

    setPhase('loading'); setErrorMsg('')
    transferDoneRef.current = false
    let internalTxId = null

    try {
      const res = await getMeshPayLinkTokenService({ symbol: currency.symbol })
      const linkToken =
        res?.data?.content?.linkToken || res?.data?.linkToken || res?.content?.linkToken || res?.linkToken
      internalTxId =
        res?.data?.content?.transactionId || res?.data?.transactionId || res?.content?.transactionId || res?.transactionId

      if (!linkToken) { setErrorMsg('Could not get session token. Please try again.'); setPhase('error'); return }

      const connection = createLink({
        renderType: 'overlay',
        theme: 'dark',
        language: 'en',
        displayFiatCurrency: 'USD',
        onIntegrationConnected: () => {},
        onTransferFinished: async (payload) => {
          try {
            const status         = (payload?.status || '').toLowerCase()
            const amount         = payload?.amount
            const symbol         = payload?.symbol
            const meshTransferId = payload?.transferId || payload?.id

            transferDoneRef.current = true
            if (['failed', 'declined'].includes(status)) {
              setErrorMsg('Transfer was declined. Please try again.'); setPhase('error'); return
            }

            setLastTransfer({
              method: 'mesh', amount, symbol, meshTransferId,
              transactionId: internalTxId, network: activeNet?.label || currency.symbol, timestamp: new Date()
            })
            setPhase('success')

            if (['succeeded', 'success', 'completed'].includes(status)) {
              try {
                await notifyMeshTransferService({ transactionId: internalTxId, status: 'succeeded', amount, symbol, meshTransferId })
                setTimeout(() => { dispatch(fetchUserInformation()); refreshBalance() }, 1500)
              } catch {}
            }
          } catch { setPhase('error'); setErrorMsg('Error processing transfer.') }
        },
        onExit: () => { if (!transferDoneRef.current) setPhase('select') },
        onEvent: () => {}
      })
      connection.openLink(linkToken)
    } catch (err) {
      setErrorMsg(err?.response?.data?.errors?.[0]?.description || err?.message || 'Could not connect. Please try again.')
      setPhase('error')
    }
  }, [user?.id, currency, activeNet, meshSupported, dispatch])

  // ── ForumPay (Banking) ──────────────────────────────────────────────────────
  const handleForumPay = useCallback(() => {
    setForumPayAmount(''); setForumPayError(''); setForumPayResult(null)
    setPhase('forumpay')
  }, [])

  const handleForumPaySubmit = useCallback(async () => {
    const amt = parseFloat(forumPayAmount)
    if (!amt || amt < 2) { setForumPayError('Minimum deposit is $2.00'); return }

    setPhase('loading'); setErrorMsg('')
    try {
      const res = await generateCryptoPaymentTokenService({
        amount: forumPayAmount,
        cryptoCurrency: forumPayCrypto
      })
      if (res?.address) {
        setForumPayResult(res)
        setPhase('forumpay-address')
      } else {
        setErrorMsg('Failed to initialize payment. Please try again.')
        setPhase('error')
      }
    } catch (err) {
      setErrorMsg(err?.message || 'Could not connect. Please try again.')
      setPhase('error')
    }
  }, [forumPayAmount, forumPayCrypto])

  // ── Interac (CAD only) ──────────────────────────────────────────────────────
  const handleInteracDeposit = async () => {
    const amt = parseFloat(interacAmount)
    if (!amt || amt <= 0) { setInteracError('Please enter a valid amount.'); return }
    if (amt < 10) { setInteracError('Minimum deposit is $10 CAD.'); return }

    setInteracLoading(true); setInteracError('')
    try {
      const action = await dispatch(generatePaymentToken({ amount: amt, transactionType: 'deposit' }))
      if (action?.payload?.token || action?.meta?.requestStatus === 'fulfilled') {
        setLastTransfer({ method: 'interac', amount: amt, symbol: 'CAD', timestamp: new Date() })
        setPhase('success')
        setTimeout(() => dispatch(fetchUserInformation()), 2000)
      } else {
        throw new Error(action?.error?.message || 'Interac deposit failed.')
      }
    } catch (err) {
      setInteracError(err?.message || 'Interac deposit failed. Please try again.')
    } finally {
      setInteracLoading(false)
    }
  }

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=${encodeURIComponent(depositAddr)}&bgcolor=ffffff&color=000000&margin=4`

  // ── Interac phase ───────────────────────────────────────────────────────────
  if (phase === 'interac') {
    return (
      <div className='cd-main'>
        <button className='cd-back-btn' type='button' onClick={reset}>
          <ArrowLeft size={14} /> Back
        </button>

        <div className='cd-interac-header'>
          <div className='cd-interac-logo-wrap'>
            <CreditCard size={22} strokeWidth={1.5} />
          </div>
          <div>
            <p className='cd-interac-title'>Interac® e-Transfer</p>
            <p className='cd-interac-sub'>Instant CAD deposits · No fees</p>
          </div>
        </div>

        <div className='cd-field'>
          <p className='cd-field-label'>Amount (CAD)</p>
          <div className='cd-interac-amount-wrap'>
            <span className='cd-interac-symbol'>$</span>
            <input
              className={`cd-interac-input ${interacError ? 'error' : ''}`}
              type='number'
              placeholder='0.00'
              value={interacAmount}
              onChange={e => { setInteracAmount(e.target.value); setInteracError('') }}
              min='10'
              step='any'
              autoFocus
            />
            <span className='cd-interac-currency'>CAD</span>
          </div>
          {interacError && <p className='cd-interac-error'>{interacError}</p>}
          <p className='cd-interac-hint'>Minimum deposit: $10.00 CAD</p>
        </div>

        <button
          className='cd-interac-submit'
          type='button'
          disabled={interacLoading || !interacAmount}
          onClick={handleInteracDeposit}
        >
          {interacLoading
            ? <><span className='cd-spin' /> Processing…</>
            : `Deposit ${interacAmount ? `$${interacAmount}` : ''} via Interac`}
        </button>
      </div>
    )
  }

  // ── ForumPay amount entry ────────────────────────────────────────────────────
  if (phase === 'forumpay') {
    const FP_CRYPTOS = ['BTC', 'ETH', 'LTC', 'USDT']
    return (
      <div className='cd-main'>
        <button className='cd-back-btn' type='button' onClick={reset}>
          <ArrowLeft size={14} /> Back
        </button>

        <div className='cd-fp-header'>
          <div className='cd-method-icon-wrap cd-method-icon-wrap--forum'>
            <Building2 size={22} strokeWidth={2} />
          </div>
          <div>
            <p className='cd-interac-title'>Banking Deposit</p>
            <p className='cd-interac-sub'>Pay with crypto via ForumPay</p>
          </div>
        </div>

        <div className='cd-field'>
          <p className='cd-field-label'>Amount (USD)</p>
          <div className={`cd-interac-amount-wrap${forumPayError ? ' cd-interac-amount-wrap--error' : ''}`}>
            <span className='cd-interac-symbol'>$</span>
            <input
              className={`cd-interac-input${forumPayError ? ' error' : ''}`}
              type='number'
              placeholder='0.00'
              value={forumPayAmount}
              onChange={e => { setForumPayAmount(e.target.value); setForumPayError('') }}
              min='2'
              step='any'
              autoFocus
            />
            <span className='cd-interac-currency'>USD</span>
          </div>
          {forumPayError && <p className='cd-interac-error'>{forumPayError}</p>}
          <p className='cd-interac-hint'>Minimum $2.00 · You pay in crypto</p>
        </div>

        <div className='cd-field'>
          <p className='cd-field-label'>Pay with</p>
          <div className='cd-fp-crypto-options'>
            {FP_CRYPTOS.map(c => (
              <button
                key={c}
                type='button'
                className={`cd-fp-crypto-btn${forumPayCrypto === c ? ' active' : ''}`}
                onClick={() => setForumPayCrypto(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <button
          className='cd-interac-submit'
          type='button'
          onClick={handleForumPaySubmit}
        >
          Get Payment Address
        </button>
      </div>
    )
  }

  // ── ForumPay payment address ─────────────────────────────────────────────────
  if (phase === 'forumpay-address') {
    const fpAddr    = forumPayResult?.address || ''
    const qrFpUrl   = forumPayResult?.qrImg || `https://api.qrserver.com/v1/create-qr-code/?size=112x112&data=${encodeURIComponent(fpAddr)}&bgcolor=ffffff&color=000000&margin=6`
    const rawWait   = Number(forumPayResult?.waitTime)
    const waitMins  = Number.isFinite(rawWait) && rawWait > 0 ? Math.ceil(rawWait / 60) : null
    const fpCoin    = CURRENCIES.find(c => c.symbol === forumPayResult?.cryptoCurrency)
    const isProcessing = pollState === 'processing'
    const statusLabel  = isProcessing ? 'Transaction Processing' : 'Awaiting Payment'
    const statusSub    = isProcessing
      ? 'Your payment was detected. Waiting for blockchain confirmations.'
      : 'Send the exact amount to the address below. This page auto-updates.'

    return (
      <div className='cd-main'>
        <button className='cd-back-btn' type='button' onClick={() => setPhase('forumpay')}>
          <ArrowLeft size={14} /> Back
        </button>

        {/* Header */}
        <div className='cd-fp-pay-header'>
          {fpCoin?.img
            ? <img src={fpCoin.img} alt={fpCoin.symbol} className='cd-fp-pay-coin-img' />
            : <div className='cd-method-icon-wrap cd-method-icon-wrap--forum'><Building2 size={18} /></div>}
          <div>
            <h4 className='cd-fp-pay-title'>{fpCoin?.name || forumPayResult?.cryptoCurrency} Deposit</h4>
            <p className='cd-fp-pay-sub'>Powered by ForumPay · Crypto payment</p>
          </div>
        </div>

        {/* Live status bar */}
        <div className={`cd-fp-status-bar${isProcessing ? ' cd-fp-status-bar--processing' : ' cd-fp-status-bar--waiting'}`}>
          <span className={`cd-fp-status-dot${isProcessing ? ' cd-fp-status-dot--processing' : ' cd-fp-status-dot--waiting'}`} />
          <div className='cd-fp-status-body'>
            <span className='cd-fp-status-label'>{statusLabel}</span>
            <span className='cd-fp-status-sub'>{statusSub}</span>
          </div>
          {forumPayPolling && <Wifi size={13} className='cd-fp-status-icon' />}
        </div>

        {/* Amount due */}
        <div className='cd-fp-amount-card'>
          <p className='cd-fp-amount-tag'>AMOUNT DUE</p>
          <div className='cd-fp-amount-row'>
            <span className='cd-fp-amount-crypto'>
              {forumPayResult?.cryptoAmount}
              <span className='cd-fp-amount-sym'>{forumPayResult?.cryptoCurrency}</span>
            </span>
            {forumPayResult?.invoiceAmount && (
              <span className='cd-fp-amount-fiat'>≈ ${forumPayResult.invoiceAmount} USD</span>
            )}
          </div>
          {forumPayResult?.exchangeRate && (
            <p className='cd-fp-rate'>
              1 {forumPayResult.cryptoCurrency} ≈ ${parseFloat(forumPayResult.exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 0 })} USD
            </p>
          )}
        </div>

        {/* Send-to address */}
        <div>
          <p className='cd-field-label' style={{ marginBottom: 6 }}>SEND TO ADDRESS</p>
          <button
            type='button'
            className='cd-fp-addr-btn'
            onClick={() => copyField(fpAddr, 'fp-addr')}
          >
            <span className='cd-address-text'>{fpAddr}</span>
            <span className='cd-fp-addr-copy'>
              {copiedField === 'fp-addr'
                ? <><CheckCircle2 size={14} color='#34d399' /> Copied</>
                : <><Copy size={14} /> Copy</>}
            </span>
          </button>
        </div>

        {/* QR + network info */}
        <div className='cd-fp-info-row'>
          <div className='cd-fp-qr-box'>
            <img src={qrFpUrl} alt='Scan to pay' width={112} height={112} className='cd-qr-img' />
            <p className='cd-fp-qr-label'>Scan to pay</p>
          </div>
          <div className='cd-fp-meta-box'>
            <div className='cd-fp-meta-item'>
              <ShieldCheck size={13} className='cd-fp-meta-icon cd-fp-meta-icon--green' />
              <div>
                <span className='cd-fp-meta-key'>Network</span>
                <span className='cd-fp-meta-val'>{fpCoin?.name || forumPayResult?.cryptoCurrency}</span>
              </div>
            </div>
            {waitMins > 0 && (
              <div className='cd-fp-meta-item'>
                <Clock size={13} className='cd-fp-meta-icon cd-fp-meta-icon--yellow' />
                <div>
                  <span className='cd-fp-meta-key'>Rate expires</span>
                  <span className='cd-fp-meta-val'>~{waitMins} min</span>
                </div>
              </div>
            )}
            <div className='cd-fp-meta-item cd-fp-meta-item--warn'>
              <AlertTriangle size={13} className='cd-fp-meta-icon cd-fp-meta-icon--red' />
              <div>
                <span className='cd-fp-meta-key'>Important</span>
                <span className='cd-fp-meta-val'>Send only {forumPayResult?.cryptoCurrency} to this address. Wrong asset = lost funds.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {forumPayResult?.accessUrl && (
          <a
            className='cd-fp-cta'
            href={forumPayResult.accessUrl}
            target='_blank'
            rel='noopener noreferrer'
          >
            <ExternalLink size={15} /> Open ForumPay Checkout
          </a>
        )}

        <button className='cd-btn-ghost' onClick={reset} style={{ marginTop: 4 }}>
          <RefreshCw size={13} /> Start a new deposit
        </button>
      </div>
    )
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className='cd-state-wrap'>
        <div className='cd-spinner-rings'>
          <span className='ring ring-1' /><span className='ring ring-2' />
          <div className='cd-spinner' />
        </div>
        <h3>Connecting…</h3>
        <p>Opening your payment gateway. Just a moment.</p>
      </div>
    )
  }

  // ── Success ─────────────────────────────────────────────────────────────────
  if (phase === 'success') {
    return (
      <div className='cd-state-wrap cd-state-wrap--receipt'>
        <div className='cd-state-icon cd-state-icon--success'>
          <CheckCircle2 size={30} strokeWidth={1.5} />
        </div>

        {lastTransfer?.symbol && (
          <div className='cd-state-amount'>
            {lastTransfer.amount != null ? lastTransfer.amount : '—'} <span>{lastTransfer.symbol}</span>
          </div>
        )}

        <h3>
          {lastTransfer?.method === 'forumpay' || (lastTransfer?.method === 'mesh' && transferStatus === 'confirmed')
            ? 'Deposit Confirmed!'
            : 'Deposit Initiated'}
        </h3>
        <p>
          {lastTransfer?.method === 'interac'
            ? 'Your Interac e-Transfer request has been submitted.'
            : lastTransfer?.method === 'forumpay'
            ? 'Your crypto deposit has been confirmed. Your balance has been updated.'
            : lastTransfer?.method === 'mesh' && transferStatus === 'confirmed'
            ? 'Your balance has been updated.'
            : 'Your funds are on the way. Balance updates once the network confirms.'}
        </p>

        <div className='cd-receipt'>
          <div className='cd-receipt-row'>
            <span className='cd-receipt-label'>Status</span>
            {lastTransfer?.method === 'forumpay'
              ? <span className='cd-receipt-badge cd-receipt-badge--success'><CheckCircle2 size={10} /> Confirmed</span>
              : lastTransfer?.method === 'mesh'
              ? transferStatus === 'confirmed'
                ? <span className='cd-receipt-badge cd-receipt-badge--success'><CheckCircle2 size={10} /> Confirmed</span>
                : transferStatus === 'failed'
                ? <span className='cd-receipt-badge cd-receipt-badge--failed'><XCircle size={10} /> Failed</span>
                : <span className='cd-receipt-badge cd-receipt-badge--pending'><Clock size={10} /> Pending…</span>
              : <span className='cd-receipt-badge cd-receipt-badge--pending'><Clock size={10} /> Pending</span>
            }
          </div>
          <div className='cd-receipt-row'>
            <span className='cd-receipt-label'>Method</span>
            <span className='cd-receipt-val'>
              {lastTransfer?.method === 'interac'   ? 'Interac® e-Transfer'
               : lastTransfer?.method === 'forumpay' ? 'Banking (ForumPay)'
               : 'Direct Crypto (Mesh)'}
            </span>
          </div>
          {lastTransfer?.network && lastTransfer?.method === 'mesh' && (
            <div className='cd-receipt-row'>
              <span className='cd-receipt-label'>Network</span>
              <span className='cd-receipt-val'>{lastTransfer.network}</span>
            </div>
          )}
          {lastTransfer?.symbol && (
            <div className='cd-receipt-row'>
              <span className='cd-receipt-label'>Asset</span>
              <span className='cd-receipt-val'>{lastTransfer.symbol}</span>
            </div>
          )}
          {lastTransfer?.transactionId && (
            <div className='cd-receipt-row'>
              <span className='cd-receipt-label'>Reference ID</span>
              <span className='cd-receipt-id-wrap'>
                <code className='cd-receipt-id'>{shortId(lastTransfer.transactionId)}</code>
                <button className='cd-receipt-copy' type='button' onClick={() => copyField(lastTransfer.transactionId, 'txid')}>
                  {copiedField === 'txid' ? <CheckCircle2 size={12} color='#34d399' /> : <Copy size={12} />}
                </button>
              </span>
            </div>
          )}
          {lastTransfer?.meshTransferId && (
            <div className='cd-receipt-row'>
              <span className='cd-receipt-label'>Transfer ID</span>
              <span className='cd-receipt-id-wrap'>
                <code className='cd-receipt-id'>{shortId(lastTransfer.meshTransferId)}</code>
                <button className='cd-receipt-copy' type='button' onClick={() => copyField(lastTransfer.meshTransferId, 'meshid')}>
                  {copiedField === 'meshid' ? <CheckCircle2 size={12} color='#34d399' /> : <Copy size={12} />}
                </button>
              </span>
            </div>
          )}
          <div className='cd-receipt-row cd-receipt-row--last'>
            <span className='cd-receipt-label'>Time</span>
            <span className='cd-receipt-val'>{formatTime(lastTransfer?.timestamp)}</span>
          </div>
        </div>

        <button className='cd-btn-ghost' onClick={reset}>
          <RefreshCw size={13} /> Make another deposit
        </button>
      </div>
    )
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className='cd-state-wrap'>
        <div className='cd-state-icon cd-state-icon--error'>
          <XCircle size={30} strokeWidth={1.5} />
        </div>
        <h3>Deposit Failed</h3>
        <p>{errorMsg || 'Something went wrong. Please try again.'}</p>
        <button className='cd-btn-ghost' onClick={reset}>
          <RefreshCw size={13} /> Try again
        </button>
      </div>
    )
  }

  // ── Main select UI ──────────────────────────────────────────────────────────
  return (
    <div className='cd-main'>

      {/* Currency + Network selectors */}
      <div className='cd-selectors'>
        <div className='cd-field'>
          <p className='cd-field-label'>Currency</p>
          <div className='cd-dropdown-wrap'>
            <button
              className='cd-dropdown-btn'
              onClick={() => { setShowCurrDrop(v => !v); setShowNetDrop(false) }}
              type='button'
            >
              <img src={currency.img} alt={currency.symbol} className='cd-coin-img' />
              <span className='cd-curr-label'>
                <strong>{currency.symbol}</strong>
                <span>{currency.name}</span>
              </span>
              <ChevronDown size={15} className={`cd-chevron ${showCurrDrop ? 'open' : ''}`} />
            </button>
            {showCurrDrop && (
              <div className='cd-dropdown-list'>
                {CURRENCIES.map(c => (
                  <button
                    key={c.symbol}
                    className={`cd-dropdown-item ${c.symbol === currency.symbol ? 'active' : ''}`}
                    onClick={() => selectCurrency(c)}
                    type='button'
                  >
                    <img src={c.img} alt={c.symbol} className='cd-coin-img' />
                    <span className='cd-curr-label'>
                      <strong>{c.symbol}</strong>
                      <span>{c.name}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className='cd-field'>
          <p className='cd-field-label'>Network</p>
          <div className='cd-dropdown-wrap'>
            <button
              className='cd-dropdown-btn'
              onClick={() => { setShowNetDrop(v => !v); setShowCurrDrop(false) }}
              type='button'
              disabled={networks.length <= 1}
            >
              <span className='cd-net-label'>{activeNet?.label}</span>
              {networks.length > 1 && (
                <ChevronDown size={15} className={`cd-chevron ${showNetDrop ? 'open' : ''}`} />
              )}
            </button>
            {showNetDrop && networks.length > 1 && (
              <div className='cd-dropdown-list'>
                {networks.map((n, i) => (
                  <button
                    key={n.id}
                    className={`cd-dropdown-item ${i === networkIdx ? 'active' : ''}`}
                    onClick={() => selectNetwork(i)}
                    type='button'
                  >
                    <span className='cd-net-label'>{n.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment method buttons */}
      <div className='cd-methods'>
        <button
          className={`cd-method-btn cd-method-btn--mesh${!meshSupported ? ' cd-method-btn--off' : ''}`}
          onClick={handleMeshPay}
          type='button'
          disabled={!meshSupported}
        >
          <div className='cd-method-top'>
            <span className='cd-method-badge'>INSTANT</span>
          </div>
          <div className='cd-method-icon-wrap cd-method-icon-wrap--mesh'>
            <Zap size={22} strokeWidth={2} />
          </div>
          <span className='cd-method-name'>Direct Crypto</span>
          <span className='cd-method-sub'>
            {meshSupported ? 'From your wallet or exchange' : `Not available for ${currency.symbol}`}
          </span>
        </button>

        <button
          className='cd-method-btn cd-method-btn--forum'
          onClick={handleForumPay}
          type='button'
        >
          <div className='cd-method-top' />
          <div className='cd-method-icon-wrap cd-method-icon-wrap--forum'>
            <Building2 size={22} strokeWidth={2} />
          </div>
          <span className='cd-method-name'>Banking</span>
          <span className='cd-method-sub'>Cards &amp; Fiat</span>
        </button>
      </div>

      {/* Interac — CAD users only */}
      {isCAD && (
        <button
          className='cd-method-btn cd-method-btn--interac cd-method-btn--full'
          onClick={() => setPhase('interac')}
          type='button'
        >
          <div className='cd-method-icon-wrap cduo-method-icon-wrap--interac'>
            <CreditCard size={20} strokeWidth={1.8} />
          </div>
          <div className='cd-method-text'>
            <span className='cd-method-name'>Interac® e-Transfer</span>
            <span className='cd-method-sub'>Instant CAD deposit · No fees</span>
          </div>
        </button>
      )}

      {/* Manual send toggle */}
      <button className='cd-manual-toggle' onClick={() => setShowManual(v => !v)} type='button'>
        <span className='cd-manual-line' />
        <span className='cd-manual-label'>
          {showManual ? 'Hide address' : 'Send manually'}
          <ChevronDown size={13} className={`cd-chevron ${showManual ? 'open' : ''}`} />
        </span>
        <span className='cd-manual-line' />
      </button>

      {showManual && (
        <div className='cd-address-box'>
          <div className='cd-address-row'>
            <span className='cd-address-text'>{depositAddr}</span>
            <button className='cd-copy-btn' onClick={handleCopy} type='button'>
              {copied ? <CheckCircle2 size={16} color='#34d399' /> : <Copy size={16} />}
            </button>
          </div>
          <div className='cd-address-bottom'>
            <div className='cd-qr-wrap'>
              <img src={qrUrl} alt={`QR for ${currency.symbol}`} width={110} height={110} className='cd-qr-img' />
            </div>
            <div className='cd-address-warning'>
              <p className='cd-warning-main'>Send only {currency.symbol} on {activeNet?.label}</p>
              <p className='cd-warning-sub'>$10.00 minimum · 1 confirmation required</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(CryptoDeposit)
